import React, { useEffect, useState } from 'react';
import styles from './TranslatorHistory.module.scss';
import { Button } from '@zurich-primitives/button';
import { ReactComponent as DownloadIcon } from 'app/assets/Icons/Download.svg';
import { getTranslatedFile, getTranslationStatus } from 'integration';
import { ITranslationJob } from './TranslatorHistory.interface';
import { LoadingSpinner } from 'app/components';
import { formatTranslatedFileName, getFileType } from 'app/utils';
import config from 'config';
import { useNavigate } from 'react-router-dom';

export const TranslatorHistory: React.FC = () => {
  const navigate = useNavigate();
  const [translationJobs, setTranslationJobs] = useState<ITranslationJob[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    setIsLoading(true);
    getTranslationStatus('cross-functional', 'translator')
      .then((response) => {
        setTranslationJobs(response?.statusFiles ?? []);
      })
      .catch((error) => {
        console.error('Error:', error);
        navigate(config.APP.PATH.LANDING);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const downloadFile = (internalFileName: string): void => {
    const requestBody = {
      fileName: internalFileName
    };

    const mimeType = getFileType(internalFileName);

    getTranslatedFile('cross-functional', 'translator', requestBody)
      .then((response) => {
        const blob = new Blob([Uint8Array.from(response.data)], {
          type: mimeType
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const formattedFileName = formatTranslatedFileName(internalFileName);
        link.download = `translated_${formattedFileName}`;
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error('Error:', error);
        navigate(config.APP.PATH.LANDING);
      });
  };

  const formatDate = (isoString: string): string => {
    const date = new Date(isoString);
    const formattedDate = date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    return formattedDate.replace(',', '');
  };

  return (
    <>
      <div className={styles['translator-history-container']}>
        <table className={styles['translator-history-table']}>
          <thead>
            <tr>
              <th>File Name</th>
              <th>Valid Until</th>
              <th> </th>
            </tr>
          </thead>
          <tbody>
            {translationJobs.length === 0 ? (
              <tr className={styles['no-data-row']}>
                <td></td>
                <td>No data available</td>
                <td></td>
              </tr>
            ) : (
              translationJobs.map((translationJob) => (
                <tr key={translationJob.id}>
                  <td>
                    {formatTranslatedFileName(translationJob.internalName)}
                  </td>
                  <td>{formatDate(translationJob.validUntil)}</td>
                  <td>
                    <Button
                      size="small"
                      onClick={() => downloadFile(translationJob.internalName)}
                    >
                      <Button.Icon>
                        <DownloadIcon />
                      </Button.Icon>
                      <Button.Label>Download</Button.Label>
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {isLoading && <LoadingSpinner />}
      </div>
      <div>
        {translationJobs.length === 0 && (
          <div className={styles['no-data-message']}>
            You haven&apos;t made any document translations yet, or your
            previous translations have expired.
            <br />
            Please note that translations expire after 24 hours.
          </div>
        )}
      </div>
    </>
  );
};
